import React, { useEffect, useState, useRef } from "react";
import {
  Card,
  Checkbox,
  Tooltip,
  Typography,
  CardContent,
  Tab,
  Tabs,
  Pagination,
  PaginationItem,
} from "@mui/material";
import { format } from "date-fns";
import LoadingSpinner from "../../components/LoadingSpinner";
import "../../components/app/Fixes.css";
import { capitalizeFirstLetter } from "../../components/reusableComponent/CapitalLetter";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  getIconClasses,
  getPriorityFlagColor,
} from "../../components/reusableComponent/IconAndBadgeClasses";
import ConfirmationModal from "../../components/reusableComponent/NonActionableConfirmationModal";

interface Ticket {
  id: number;
  title: string;
  created: string;
  status_type: string;
  description: string;
  assignee: string;
  raisedBy: string;
  priority: string;
  category: string;
  dueDate: string;
}

interface IProps {
  ticket: { results: Ticket[]; count: number };
  currentPage: number;
  setCurrentPage: any;
  selectedTickets: any;
  setSelectedTickets: any;
  ticketWorkflow: any;
  value: any;
  setValue: any;
  setStoreKey: any;
  setLiftUiData: any;
  pageSize?: number;
  setPageSize?: (size: number) => void;
  onPageChange?: (page: number, size: number) => void;
  isTicketLoading: any;
  onNonActionable?: (ticketId: string | number, currentValue: boolean) => void;
}

const TicketCardView: React.FC<IProps> = ({
  ticket,
  currentPage,
  setCurrentPage,
  selectedTickets,
  setSelectedTickets,
  ticketWorkflow,
  value,
  setValue,
  setStoreKey,
  setLiftUiData,
  pageSize: propPageSize,
  setPageSize: propSetPageSize,
  onPageChange,
  isTicketLoading,
  onNonActionable,
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [internalPageSize, setInternalPageSize] = useState<number>(
    propPageSize || 50,
  );
  const [selectAll, setSelectAll] = useState(false);
  const [uiData, setUiData] = useState([]);
  const [keyState, setKeyState] = useState([]);
  const [ticketTabLoading, setTicketTabLoading] = useState<boolean>(false);

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const ticketCardRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const scrollRestoreCleanupRef = useRef<(() => void) | null>(null);

  const pageSize = propPageSize || internalPageSize;
  const setPageSize = propSetPageSize || setInternalPageSize;

  const ticketCount = ticket?.count ? ticket?.count : 0;
  const navigate = useNavigate();
  const location = useLocation();

  const pageSizeOptions = [25, 50, 100, 200];

  // ✅ Confirmation modal state
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [confirmPayload, setConfirmPayload] = useState<{
    ticketId: string | number;
    currentValue: boolean;
  } | null>(null);

  const rows =
    ticket?.results?.map((t: any) => ({
      id: t?.id,
      title: t?.title,
      created: format(new Date(t?.created), "yyyy-MM-dd HH:mm"),
      status_type: t?.status_type,
      displayName: t?.status_display,
      description: t?.description,
      assignee: t?.action?.assignee?.username,
      raisedBy: t?.created_by,
      priority: t?.action?.disposition?.priority,
      category: t?.action?.disposition?.workflow?.name,
      dueDate: t?.dueDate,
      ticket_channel: t?.ticket_channel,
      is_non_actionable: t?.is_non_actionable ?? false,
    })) || [];

  const storeScrollPosition = (ticketId: string) => {
    const el = scrollContainerRef.current;
    if (el)
      sessionStorage.setItem(
        `ticketCardScrollPosition_${value}`,
        String(el.scrollTop),
      );
    sessionStorage.setItem(`lastViewedTicket_Card_${value}`, ticketId);
  };

  const restoreScrollPosition = () => {
    const saved = sessionStorage.getItem(`ticketCardScrollPosition_${value}`);
    if (!saved) return () => {};
    const target = parseInt(saved, 10);
    if (Number.isNaN(target) || target < 0) return () => {};
    const apply = () => {
      const el = scrollContainerRef.current;
      if (el) el.scrollTop = target;
    };
    const delays = [0, 50, 150, 300, 500, 800, 1200, 1800, 2500, 3500];
    const timeouts = delays.map((d) => setTimeout(apply, d));
    requestAnimationFrame(() => requestAnimationFrame(apply));
    return () => timeouts.forEach((t) => clearTimeout(t));
  };

  const clearScrollPosition = () => {
    sessionStorage.removeItem(`ticketCardScrollPosition_${value}`);
    sessionStorage.removeItem(`lastViewedTicket_Card_${value}`);
  };

  const handleSelect = (
    event: React.ChangeEvent<HTMLInputElement>,
    id: number,
  ) => {
    const updated = event.target.checked
      ? [...selectedTickets, id]
      : selectedTickets.filter((tid) => tid !== id);
    setSelectedTickets(updated);
    if (!event.target.checked) setSelectAll(false);
  };

  const totalPages = Math.ceil(ticketCount / pageSize);

  const getStatusStyle = (status_type: string) => {
    switch (status_type) {
      case "active":
        return { backgroundColor: "#0d6efd", color: "white" };
      case "closed":
        return { backgroundColor: "#198754", color: "white" };
      case "reopen":
        return { backgroundColor: "#fd7e14", color: "white" };
      case "wip":
        return { backgroundColor: "#ffc107", color: "black" };
      default:
        return { backgroundColor: "#6c757d", color: "white" };
    }
  };

  const truncateDescription = (description: string, maxLength: number) => {
    if (description?.length > maxLength)
      return description?.slice(0, maxLength) + "...";
    return description;
  };

  const processData = async () => {
    const uiFormattedData = [];
    const keysForState = [];
    if (ticketWorkflow && typeof ticketWorkflow === "object") {
      await Object.entries(ticketWorkflow)?.forEach(([key, val]) => {
        if (typeof val === "object" && val !== null && !Array.isArray(val)) {
          Object.entries(val).forEach(([nk, nv]) => {
            uiFormattedData.push({ key: nk, value: nv });
            keysForState.push(key);
          });
        } else if (Array.isArray(val)) {
          val.forEach((item) => {
            if (item && typeof item === "object")
              Object.entries(item).forEach(([nk, nv]) => {
                uiFormattedData.push({ key: nk, value: nv });
                keysForState.push(key);
              });
          });
        }
      });
      setUiData(uiFormattedData);
      setKeyState(keysForState);
    } else {
      console.error("ticketWorkflow is undefined or null");
    }
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectAll(event.target.checked);
    if (event.target.checked && ticket?.results.length > 0)
      setSelectedTickets(ticket?.results.map((row) => row.id));
    else setSelectedTickets([]);
  };

  const formatDate = (dateString: string) =>
    format(new Date(dateString), "dd-MM-yyyy HH:mm:ss");

  const handlePageSizeChange = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    const newPageSize = parseInt(event.target.value);
    setPageSize(newPageSize);
    setCurrentPage(1);
    clearScrollPosition();
    sessionStorage.setItem("ticketPageSize", newPageSize.toString());
    onPageChange?.(1, newPageSize);
  };

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    page: number,
  ) => {
    clearScrollPosition();
    setCurrentPage(page);
    onPageChange?.(page, pageSize);
  };

  // ✅ Fires after user clicks "Confirm"
  const handleConfirmNonActionable = () => {
    if (confirmPayload && onNonActionable) {
      onNonActionable(confirmPayload.ticketId, confirmPayload.currentValue);
    }
    setConfirmPayload(null);
  };

  useEffect(() => {
    const savedPageSize = sessionStorage.getItem("ticketPageSize");
    if (savedPageSize && !propPageSize)
      setInternalPageSize(Number(savedPageSize));
  }, [propPageSize]);

  useEffect(() => {
    processData();
  }, [ticketWorkflow]);

  useEffect(() => {
    if (ticket && ticket.results) {
      if (
        ticket?.results.length > 0 &&
        selectedTickets.length === ticket.results.length
      )
        setSelectAll(true);
      else setSelectAll(false);
    } else {
      setSelectAll(false);
    }
  }, [selectedTickets, ticket]);

  useEffect(() => {
    setLoading(true);
    setTimeout(() => setLoading(false), 3000);
  }, [currentPage]);

  useEffect(() => {
    const canRestore =
      !loading && !ticketTabLoading && ticket?.results?.length > 0;
    if (canRestore) {
      const timer = setTimeout(() => {
        const cleanup = restoreScrollPosition();
        if (typeof cleanup === "function")
          scrollRestoreCleanupRef.current = cleanup;
      }, 50);
      return () => {
        clearTimeout(timer);
        scrollRestoreCleanupRef.current?.();
        scrollRestoreCleanupRef.current = null;
      };
    }
  }, [loading, ticketTabLoading, ticket?.results, value]);

  useEffect(() => {
    setLiftUiData(uiData);
  }, [uiData]);
  useEffect(() => {
    setTicketTabLoading(true);
    setTimeout(() => setTicketTabLoading(false), 3000);
  }, [value]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      const el = scrollContainerRef.current;
      if (el)
        sessionStorage.setItem(
          `ticketCardScrollPosition_${value}`,
          String(el.scrollTop),
        );
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [value]);

  useEffect(() => {
    if (!location.pathname.includes("/ticket")) clearScrollPosition();
  }, [location.pathname]);

  return (
    <>
      {loading ? (
        <LoadingSpinner />
      ) : (
        <>
          <div className="tabs-header-section">
            <div className="tabs-header-content">
              <Tabs
                value={uiData.findIndex((item) => item.key === value)}
                onChange={(event, newValue) => {
                  clearScrollPosition();
                  setStoreKey(keyState[newValue]);
                  setValue(uiData[newValue].key);
                  setCurrentPage(1);
                }}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  flex: 1,
                  minHeight: "44px",
                  backgroundColor: "#ffffff",
                  "& .MuiTabs-indicator": {
                    backgroundColor: "#6366f1",
                    height: "2px",
                  },
                  "& .MuiTab-root": {
                    textTransform: "none",
                    minHeight: "44px",
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "#6c757d",
                    padding: "10px 20px",
                    transition: "all 0.2s ease",
                    minWidth: "auto",
                    "&:hover": { color: "#495057", backgroundColor: "#f8f9fa" },
                    "&.Mui-selected": { color: "#6366f1", fontWeight: 600 },
                  },
                  "& .MuiTabs-scrollButtons": {
                    color: "#6c757d",
                    "&.Mui-disabled": { opacity: 0.3 },
                  },
                }}
              >
                {uiData.map((ticketsInfo, index) => (
                  <Tooltip
                    key={index}
                    title={`${ticketsInfo.key} (${ticketsInfo.value})`}
                    arrow
                  >
                    <Tab
                      key={index}
                      label={truncateDescription(
                        `${ticketsInfo.key} (${ticketsInfo.value})`,
                        25,
                      )}
                    />
                  </Tooltip>
                ))}
              </Tabs>
            </div>
          </div>

          <div
            ref={scrollContainerRef}
            style={{
              height: "calc(100vh - 400px)",
              overflow: "auto",
              overflowX: "hidden",
            }}
          >
            {ticketTabLoading ? (
              <LoadingSpinner />
            ) : (
              <div
                className="tab-content d-flex"
                style={{
                  position: "relative",
                  width: "100%",
                  overflow: "scroll",
                }}
              >
                <div className="row g-9">
                  <div className="col-md-12">
                    <div
                      style={{
                        marginLeft: "40px",
                        display: "grid",
                        gridTemplateColumns: "repeat(5, 1fr)",
                        gap: "16px",
                        padding: "16px 0",
                      }}
                    >
                      <input
                        type="checkbox"
                        className="mb-xl-9 h-100"
                        style={{
                          marginTop: "18px",
                          position: "absolute",
                          top: "-43%",
                          left: "14px",
                          backgroundColor: "black",
                          cursor: "pointer",
                          width: "15px",
                          borderColor: "black",
                          borderWidth: "4px",
                          borderStyle: "solid",
                        }}
                        checked={selectAll}
                        onChange={handleSelectAll}
                      />

                      {rows?.map((row: any) => {
                        return (
                          <div
                            key={row.id}
                            ref={(el) => (ticketCardRefs.current[row.id] = el)}
                            className="d-flex flex-column mx-5 p-2"
                            style={{ flex: "1 1 340px", minWidth: "340px" }}
                          >
                            <Link
                              to={`/ticket/information/${row.id}`}
                              className="fs-5 fw-bold flex-grow-1"
                              style={{
                                textDecoration: "none",
                                color: "inherit",
                              }}
                              onClick={() =>
                                storeScrollPosition(String(row.id))
                              }
                            >
                              <Card
                                className="mb-6 mb-xl-9"
                                style={{
                                  minHeight: "350px",
                                  display: "flex",
                                  flexDirection: "column",
                                  transition:
                                    "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.transform =
                                    "translateY(-2px)";
                                  e.currentTarget.style.boxShadow =
                                    "0 4px 12px rgba(0,0,0,0.15)";
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.transform =
                                    "translateY(0)";
                                  e.currentTarget.style.boxShadow =
                                    "0 2px 4px rgba(0,0,0,0.1)";
                                }}
                              >
                                <CardContent>
                                  {/* Top row: checkbox + Ref ID + non-actionable toggle */}
                                  <div
                                    className="d-flex align-items-center justify-content-between mb-3"
                                    style={{ marginLeft: "-10px" }}
                                  >
                                    <div className="d-flex align-items-center">
                                      <Checkbox
                                        checked={selectedTickets.includes(
                                          row.id,
                                        )}
                                        onChange={(event) =>
                                          handleSelect(event, row.id)
                                        }
                                        onClick={(e) => e.stopPropagation()}
                                      />
                                      <Typography className="fs-6 fw-semibold text-gray-600">
                                        {row.id}
                                      </Typography>
                                    </div>

                                    {/* ✅ Non-actionable toggle — opens shared ConfirmationModal */}
                                    {onNonActionable && (
                                      <Tooltip
                                        title={
                                          row.is_non_actionable
                                            ? "Mark as actionable"
                                            : "Mark as non-actionable"
                                        }
                                        arrow
                                      >
                                        <button
                                          style={{
                                            background: "none",
                                            border: "none",
                                            cursor: "pointer",
                                            padding: "4px",
                                            marginRight: "4px",
                                            display: "inline-flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            width: 28,
                                            height: 28,
                                          }}
                                          onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            setConfirmPayload({
                                              ticketId: row.id,
                                              currentValue:
                                                row.is_non_actionable,
                                            });
                                            setConfirmModalOpen(true);
                                          }}
                                          aria-label={
                                            row.is_non_actionable
                                              ? "Mark as actionable"
                                              : "Mark as non-actionable"
                                          }
                                        >
                                          <i
                                            className={`bi ${
                                              row.is_non_actionable
                                                ? "bi bi-ticket-perforated-fill fs-2"
                                                : "bi bi-ticket-perforated fs-2"
                                            }`}
                                            style={{
                                              color: row.is_non_actionable
                                                ? "#059669"
                                                : "#6B7280",
                                              fontSize: "16px",
                                            }}
                                          />
                                        </button>
                                      </Tooltip>
                                    )}
                                  </div>

                                  <Typography
                                    className="fs-6 fw-semibold text-gray-600 mb-3"
                                    data-bs-toggle="tooltip"
                                    title={row.title}
                                  >
                                    Title: {truncateDescription(row.title, 28)}
                                  </Typography>

                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                      marginBottom: "10px",
                                    }}
                                  >
                                    <div
                                      className="badge badge-light"
                                      style={{
                                        ...getStatusStyle(row.status_type),
                                      }}
                                      data-bs-toggle="tooltip"
                                      title={row.displayName}
                                    >
                                      <span className="fw-bold">
                                        Status:{" "}
                                        {truncateDescription(
                                          row.displayName,
                                          10,
                                        )}
                                      </span>
                                    </div>
                                    <div
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "10px",
                                      }}
                                    >
                                      <div
                                        style={{ cursor: "pointer" }}
                                        title={`Priority: ${capitalizeFirstLetter(
                                          row.priority,
                                        )}`}
                                      >
                                        <i
                                          className="bi bi-flag-fill"
                                          style={{
                                            color: getPriorityFlagColor(
                                              row.priority,
                                            ),
                                            fontSize: "15px",
                                          }}
                                        ></i>
                                      </div>
                                    </div>
                                  </div>

                                  <Typography className="fs-6 fw-semibold text-gray-600 mb-3">
                                    Ticket channel:{" "}
                                    <i
                                      className={`text-dark mx-2 ${getIconClasses(
                                        row?.ticket_channel,
                                      )}`}
                                      data-bs-toggle="tooltip"
                                      title={row?.ticket_channel}
                                    ></i>
                                  </Typography>

                                  {row.description && (
                                    <Typography
                                      className="fs-6 fw-semibold text-gray-600 mb-3"
                                      title={row.description}
                                    >
                                      Description:
                                      <div className="badge badge-light">
                                        {truncateDescription(
                                          row.description,
                                          30,
                                        )}
                                      </div>
                                    </Typography>
                                  )}

                                  <Typography className="fs-6 fw-semibold text-gray-600 mb-3">
                                    Created: {formatDate(row.created)}
                                  </Typography>
                                  <Typography className="fs-6 fw-semibold text-gray-600 mb-3">
                                    Assignee:{" "}
                                    {capitalizeFirstLetter(
                                      row.assignee === undefined
                                        ? "Unassigned"
                                        : row.assignee,
                                    )}
                                  </Typography>
                                  <Typography className="fs-6 fw-semibold text-gray-600 mb-3">
                                    Raised by:{" "}
                                    {capitalizeFirstLetter(row?.raisedBy?.username || row?.raisedBy || "-")}
                                  </Typography>
                                  <Typography className="fs-6 fw-semibold text-gray-600 mb-5">
                                    Priority:{" "}
                                    <span
                                      style={{
                                        color: getPriorityFlagColor(
                                          row.priority,
                                        ),
                                      }}
                                    >
                                      {capitalizeFirstLetter(row.priority)}
                                    </span>
                                  </Typography>
                                </CardContent>
                              </Card>
                            </Link>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* Pagination */}
      <div style={{ position: "sticky", bottom: 0, zIndex: 10 }}>
        <div
          className="d-flex justify-content-between align-items-center mt-4 px-2"
          style={{ marginBottom: "8px" }}
        >
          <div className="d-flex align-items-center gap-2">
            <span style={{ fontSize: "14px", whiteSpace: "nowrap" }}>Show</span>
            <select
              className="form-select form-select-sm"
              style={{ width: "60px", fontSize: "14px", padding: "2px 6px" }}
              name="perpage"
              value={pageSize}
              onChange={handlePageSizeChange}
            >
              {pageSizeOptions.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
            <span
              style={{ fontSize: "14px", fontWeight: 500, color: "#495057" }}
            >
              per page
            </span>
          </div>
          <div
            className="d-flex align-items-center"
            style={{ paddingRight: "8px" }}
          >
            <Pagination
              sx={{
                display: "flex",
                justifyContent: "end",
                "& .MuiPaginationItem-root": {
                  fontSize: "14px",
                  minWidth: "36px",
                  height: "36px",
                  margin: "0 2px",
                  borderRadius: "6px",
                  border: "1px solid #e5e7eb",
                  color: "#374151",
                  "&:hover": {
                    backgroundColor: "#f9fafb",
                    borderColor: "#d1d5db",
                  },
                  "&.Mui-selected": {
                    backgroundColor: "#6366f1",
                    color: "#ffffff",
                    borderColor: "#6366f1",
                    "&:hover": {
                      backgroundColor: "#4f46e5",
                      borderColor: "#4f46e5",
                    },
                  },
                },
              }}
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
              showFirstButton
              showLastButton
              renderItem={(item) => {
                if (item.type === "page") {
                  const near =
                    Math.abs(item.page - currentPage) <= 1 ||
                    item.page === 1 ||
                    item.page === totalPages;
                  return near ? <PaginationItem {...item} /> : null;
                }
                return <PaginationItem {...item} />;
              }}
            />
          </div>
        </div>
      </div>

      {/* ✅ Single reusable ConfirmationModal — rendered once, outside the map */}
      <ConfirmationModal
        isOpen={confirmModalOpen}
        onClose={() => {
          setConfirmModalOpen(false);
          setConfirmPayload(null);
        }}
        onConfirm={handleConfirmNonActionable}
        title="Confirm Action"
        message={
          <>
            Are you sure you want to{" "}
            <strong>
              {confirmPayload?.currentValue
                ? "mark as actionable"
                : "mark as non-actionable"}
            </strong>
            ?
          </>
        }
      />
    </>
  );
};

export default TicketCardView;